import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  X,
  Plus,
  Search,
  Mail,
  Handshake,
  Calendar,
  FileText,
  Heart,
  Trash2,
  Edit2,
  Check,
  Loader2,
  ChevronRight,
} from 'lucide-react';

interface EmailTemplatesProps {
  onSelect: (template: { subject: string; body: string }) => void;
  onClose: () => void;
}

interface Template {
  id: number;
  name: string;
  subject: string;
  body: string;
  category: string;
  is_default: boolean;
}

const categoryIcons: Record<string, React.ReactNode> = {
  outreach: <Mail className="h-4 w-4" />,
  'follow-up': <Handshake className="h-4 w-4" />,
  meeting: <Calendar className="h-4 w-4" />,
  proposal: <FileText className="h-4 w-4" />,
  thankyou: <Heart className="h-4 w-4" />,
  general: <Mail className="h-4 w-4" />,
};

const categoryColors: Record<string, string> = {
  outreach: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  'follow-up': 'bg-green-500/10 text-green-600 dark:text-green-400',
  meeting: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  proposal: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  thankyou: 'bg-pink-500/10 text-pink-600 dark:text-pink-400',
  general: 'bg-slate-500/10 text-muted-foreground dark:text-slate-400',
};

const categoryLabels: Record<string, string> = {
  outreach: 'Outreach',
  'follow-up': 'Follow-up',
  meeting: 'Meeting',
  proposal: 'Proposal',
  thankyou: 'Thank You',
  general: 'General',
};

export function EmailTemplates({ onSelect, onClose }: EmailTemplatesProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch('/api/crm/communications/templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...Array.from(new Set(templates.map((t) => t.category)))];

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(`/api/crm/communications/templates/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setTemplates((prev) => prev.filter((t) => t.id !== id));
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#2B7FFF]" />
              Email Templates
            </DialogTitle>
            <Button onClick={handleCreate} size="sm" className="bg-[#2B7FFF] hover:bg-[#1A5FCC]">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </DialogHeader>

        <div className="flex h-[600px]">
          {/* Sidebar */}
          <div className="w-64 border-r bg-muted/30">
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="pl-9"
                />
              </div>
            </div>

            <ScrollArea className="h-[calc(100%-80px)]">
              <div className="px-2 pb-4 space-y-1">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                      selectedCategory === category
                        ? 'bg-[#2B7FFF] text-white'
                        : 'hover:bg-muted'
                    )}
                  >
                    {category === 'all' ? (
                      <Mail className="h-4 w-4" />
                    ) : (
                      categoryIcons[category] || <Mail className="h-4 w-4" />
                    )}
                    <span className="flex-1 text-left capitalize">
                      {categoryLabels[category] || category}
                    </span>
                    <Badge
                      variant={selectedCategory === category ? 'secondary' : 'outline'}
                      className={cn(
                        'text-xs',
                        selectedCategory === category && 'bg-background/20 text-white'
                      )}
                    >
                      {
                        category === 'all'
                          ? templates.length
                          : templates.filter((t) => t.category === category).length
                      }
                    </Badge>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Template List */}
          <div className="flex-1">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#2B7FFF]" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Mail className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium">No templates found</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  {searchQuery
                    ? 'Try adjusting your search'
                    : 'Create your first template to get started'}
                </p>
              </div>
            ) : (
              <ScrollArea className="h-full">
                <div className="p-4 space-y-3">
                  {filteredTemplates.map((template, index) => (
                    <motion.div
                      key={template.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        className={cn(
                          'group cursor-pointer transition-all hover:shadow-md',
                          previewTemplate?.id === template.id && 'ring-2 ring-[#2B7FFF]'
                        )}
                        onClick={() => setPreviewTemplate(template)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{template.name}</h4>
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    'text-xs',
                                    categoryColors[template.category]
                                  )}
                                >
                                  {categoryLabels[template.category] || template.category}
                                </Badge>
                                {template.is_default && (
                                  <Badge variant="outline" className="text-xs">
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 truncate">
                                {template.subject}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!template.is_default && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(template.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(template);
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-[#2B7FFF]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelect({
                                    subject: template.subject,
                                    body: template.body,
                                  });
                                }}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Preview Panel */}
          <AnimatePresence>
            {previewTemplate && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="w-80 border-l bg-muted/30"
              >
                <div className="p-4 border-b flex items-center justify-between">
                  <h4 className="font-medium">Preview</h4>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPreviewTemplate(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="h-[calc(100%-60px)]">
                  <div className="p-4 space-y-4">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">
                        Subject
                      </span>
                      <p className="text-sm mt-1">{previewTemplate.subject}</p>
                    </div>
                    <Separator />
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">
                        Body
                      </span>
                      <div className="mt-2 p-3 bg-card rounded-md border text-sm whitespace-pre-wrap">
                        {previewTemplate.body}
                      </div>
                    </div>
                    <Button
                      onClick={() =>
                        onSelect({
                          subject: previewTemplate.subject,
                          body: previewTemplate.body,
                        })
                      }
                      className="w-full bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Use This Template
                    </Button>
                  </div>
                </ScrollArea>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Template Editor Modal */}
        {showEditor && (
          <TemplateEditor
            template={editingTemplate}
            onClose={() => setShowEditor(false)}
            onSave={(template) => {
              if (editingTemplate) {
                setTemplates((prev) =>
                  prev.map((t) => (t.id === template.id ? template : t))
                );
              } else {
                setTemplates((prev) => [...prev, template]);
              }
              setShowEditor(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface TemplateEditorProps {
  template: Template | null;
  onClose: () => void;
  onSave: (template: Template) => void;
}

function TemplateEditor({ template, onClose, onSave }: TemplateEditorProps) {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [body, setBody] = useState(template?.body || '');
  const [category, setCategory] = useState(template?.category || 'general');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !body.trim()) return;

    setIsSaving(true);
    try {
      const url = template
        ? `/api/crm/communications/templates/${template.id}`
        : '/api/crm/communications/templates';
      const method = template ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, subject, body, category }),
      });

      if (response.ok) {
        const data = await response.json();
        onSave({
          id: template?.id || data.template.id,
          name,
          subject,
          body,
          category,
          is_default: template?.is_default || false,
        });
      }
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const variables = [
    '{{firstName}}',
    '{{lastName}}',
    '{{company}}',
    '{{title}}',
    '{{senderName}}',
    '{{senderCompany}}',
    '{{senderTitle}}',
    '{{senderPhone}}',
  ];

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-body') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.substring(0, start) + variable + body.substring(end);
    setBody(newBody);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + variable.length,
        start + variable.length
      );
    }, 0);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {template ? 'Edit Template' : 'New Template'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Template Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Introduction Email"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Category</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(categoryLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setCategory(key)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm transition-colors',
                    category === key
                      ? 'bg-[#2B7FFF] text-white'
                      : 'bg-muted hover:bg-muted/80'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Subject Line</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject line..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Email Body</label>
            <div className="flex flex-wrap gap-1 pb-2">
              {variables.map((v) => (
                <button
                  key={v}
                  onClick={() => insertVariable(v)}
                  className="text-xs px-2 py-1 rounded bg-[#2B7FFF]/10 text-[#2B7FFF] hover:bg-[#2B7FFF]/20 transition-colors"
                >
                  {v}
                </button>
              ))}
            </div>
            <Textarea
              id="template-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your template here..."
              className="min-h-[200px]"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || !subject.trim() || !body.trim()}
            className="bg-[#2B7FFF] hover:bg-[#1A5FCC]"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {template ? 'Update' : 'Create'} Template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EmailTemplates;

